import { useEffect, useState } from 'react'
import {
	Button,
	Checkbox,
	CountDown,
	CountUp,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	EpicProgress,
	EpicToaster,
	ErrorList,
	HouseMark,
	Icon,
	Input,
	InputOTP,
	InputOTPGroup,
	InputOTPSlot,
	Label,
	Logo,
	Odometer,
	Spacer,
	Textarea,
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
	iconNames,
} from '#app/components/atoms'

import {
	CheckboxField,
	Field,
	ImageField,
	InstallAppMenuItem,
	OTPField,
	SeatMeter,
	StatusButton,
	TextareaField,
	TicketQr,
} from '#app/components/molecules'

import {
	Countdown,
	DetailCtaCard,
	DetailHero,
	DoorPassDialog,
	EventCard,
	EventDescription,
	EventForm,
	FestivalFooter,
	FestivalGate,
	FestivalHero,
	FestivalStatus,
	FestivalWelcome,
	getSceneVenues,
	HelpCard,
	NextEventFab,
	PerformerForm,
	ProgramMap,
	ProgramSection,
	SiteDock,
	SiteHeader,
	StickyCtaBar,
	TicketInfo,
	TicketWallet,
	VenueNightMap,
} from '#app/components/organisms'

import { ErrorMessage, EventDetailLayout } from '#app/components/templates'
import { useNow } from '#app/utils/motion.ts'
import eventCardStyleUrl from '../../styles/event-card.css?url'
import festivalStyleUrl from '../../styles/festival.css?url'
import walletStyleUrl from '../../styles/wallet.css?url'

export const links = () => [
	{ rel: 'stylesheet', href: festivalStyleUrl },
	{ rel: 'stylesheet', href: eventCardStyleUrl },
	{ rel: 'stylesheet', href: walletStyleUrl },
]

const HOUR = 60 * 60 * 1000
const inDays = (d: number) => new Date(Date.now() + d * 24 * HOUR)

const demoPerformer = {
	id: 'showcase-performer-1',
	slug: 'arne-skorpe-sjoen',
	name: 'Arne Skorpe Sjøen',
	shortBio: 'Visesongar frå Vorrå.',
	imageUrl: null,
}

const baseEvent = {
	id: 'showcase-1',
	title: 'Kveldskonsert i Lade kirke',
	date: inDays(30),
	description:
		'Ein **stemningsfull** kveld med songar frå Nord-Trøndelag.\n\n- Solo\n- Kor\n\nMed pausar og allsong.',
	shortDescription: 'Songar frå Nord-Trøndelag.',
	imageUrl: null,
	location: 'Vertshuset - Lade kirke',
	host: 'Kari Nordmann',
	performers: null,
	featuring: [demoPerformer],
	tickets: [],
	seats: { total: 14 },
}

const demoEvent = baseEvent as never
// on sale: only PER_CONCERT festivals sell, each concert at its own prices
const onSaleEvent = {
	...baseEvent,
	adultPrice: 200,
	studentPrice: 100,
	generousPrice: 300,
	childPrice: 0,
	festival: { pricingModel: 'PER_CONCERT' },
} as never
const soldOutEvent = {
	...baseEvent,
	id: 'showcase-soldout',
	date: inDays(30),
	tickets: Array.from({ length: 14 }, (_, i) => ({
		id: `t${i}`,
		type: 'adult',
		name: `Person ${i}`,
		usedAt: null,
		used: false,
		hash: `hash${i}${i}${i}`,
		paymentStatus: 'PAID',
		amountPaid: 250,
	})),
} as never
const passedEvent = {
	...baseEvent,
	id: 'showcase-passed',
	date: inDays(-30),
} as never
const demoHash = 'abcdef1234567890'

// 4 events spread over 2 dates, distinct ids/titles — feeds ProgramSection
// and ProgramMap.
const demoProgram = [
	{
		...baseEvent,
		id: 'showcase-p1',
		title: 'Kveldskonsert i Lade kirke',
		date: inDays(30),
	},
	{
		...baseEvent,
		id: 'showcase-p2',
		title: 'Salmar og song i kapellet',
		date: new Date(inDays(30).getTime() + 6 * HOUR),
	},
	{
		...baseEvent,
		id: 'showcase-p3',
		title: 'Folkemusikkveld på låven',
		date: inDays(31),
		location: 'Låven på Lade gård',
	},
	{
		...baseEvent,
		id: 'showcase-p4',
		title: 'Allsong rundt bålet',
		date: new Date(inDays(31).getTime() + 6 * HOUR),
		location: 'Bålplassen ved fjorden',
	},
] as never

// The front page's organisms, fed with the real 2024 program (titles,
// venues, seat counts and photos from huskonsertfestivalen.no), moved to
// start ten days from now so the countdown and seat states stay current.
const festivalStart = new Date(inDays(10).setUTCHours(18, 0, 0, 0))
const at = (hours: number) => new Date(festivalStart.getTime() + hours * HOUR)
const photo = (id: string) =>
	`https://huskonsertfestivalen.no/resources/note-images/${id}`
const festival2024Program = [
	{
		id: 'showcase-f1',
		title: 'Salmer ved Sjøen',
		date: at(0),
		location: 'Vorråvegen 27, 4265 Håvik',
		host: 'Liv Olen og Gunnar',
		shortDescription:
			'Kjende, mindre kjende og heilt nyskrivne salmar — sungne av gode songarar frå bygda.',
		imageUrl: photo('cm1pgar7d0005b230dohwv2ry'),
		performers: 'Svend Egil Håvardsen, Hallvard Folkedal, Arne Sjøen',
		seats: { total: 43 },
		ticketCount: 43,
	},
	{
		id: 'showcase-f2',
		title: 'Splitkonsert 1',
		date: at(2.5),
		location: 'Liarvegen 51, 4265 Håvik',
		host: 'Sissel og Steinar',
		shortDescription:
			'Jazzduoen Folkedal og Gismervik, og visesongar Arne Skorpe Sjøen.',
		imageUrl: photo('cm28vyjix000fw4qchhftarhy'),
		seats: { total: 45 },
		ticketCount: 27,
	},
	{
		id: 'showcase-f3',
		title: 'Splitkonsert 2',
		date: at(23),
		location: 'Naustet på Vorråstranda',
		shortDescription:
			'Ingvill og Tim Bolga — melankolsk indie-folk til haustmørkret.',
		imageUrl: photo('cm29j7o4t000ekv7n7wjbezde'),
		seats: { total: 26 },
		ticketCount: 23,
	},
	{
		id: 'showcase-f4',
		title: 'Soul og Suppe',
		date: at(25),
		location: 'Liarvegen 36b, 4265 Håvik',
		host: 'Berit Johanne og Lars Øystein',
		shortDescription:
			'Klassiske soullåtar og ei rykande varm suppe — i éin pakke.',
		imageUrl: photo('cm28we283000ow4qcme288qjz'),
		seats: { total: 50 },
		ticketCount: 40,
	},
]
const festival2024 = {
	pricingModel: 'TIERED',
	priceOne: 200,
	priceTwo: 300,
	priceThree: 400,
	priceFour: 500,
}

const demoPerformerFull = {
	...demoPerformer,
	bio: '## Om Arne\n\nVisesongar frå Vorrå, har spelt på huskonsertar sidan 2019.',
}

const buttonVariantsList = [
	'default',
	'destructive',
	'outline',
	'secondary',
	'ghost',
	'link',
] as const
const buttonSizesList = ['default', 'wide', 'sm', 'lg', 'pill', 'icon'] as const

function Atoms() {
	return (
		<section id="atoms" className="flex flex-col gap-8">
			<h2 className="text-2xl font-bold">Atoms</h2>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">Button</h3>
				<div className="flex flex-col gap-3">
					{buttonVariantsList.map((variant) => (
						<div key={variant} className="flex flex-wrap items-center gap-2">
							{buttonSizesList.map((size) => (
								<Button key={size} variant={variant} size={size}>
									{size === 'icon' ? (
										<Icon name="sun" title={variant} />
									) : (
										`${variant} / ${size}`
									)}
								</Button>
							))}
						</div>
					))}
				</div>
			</div>

			<div className="flex flex-wrap gap-4">
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">Input</h3>
					<Input placeholder="Skriv inn noko…" />
				</div>
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">Textarea</h3>
					<Textarea placeholder="Meir tekst…" />
				</div>
			</div>

			<div className="flex flex-wrap gap-4">
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">Checkbox + Label</h3>
					<div className="flex items-center gap-2">
						<Checkbox id="showcase-checkbox" />
						<Label htmlFor="showcase-checkbox">Hugs meg</Label>
					</div>
				</div>
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">Label</h3>
					<Label htmlFor="showcase-label-input">E-post</Label>
					<Input id="showcase-label-input" placeholder="post@eksempel.no" />
				</div>
			</div>

			<div className="flex flex-wrap gap-4">
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">InputOTP</h3>
					<InputOTP maxLength={6}>
						<InputOTPGroup>
							{[0, 1, 2, 3, 4, 5].map((index) => (
								<InputOTPSlot key={index} index={index} />
							))}
						</InputOTPGroup>
					</InputOTP>
				</div>
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">Tooltip</h3>
					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger asChild>
								<Button variant="outline">Hover meg</Button>
							</TooltipTrigger>
							<TooltipContent>
								<p>Ei lita forklaring dukkar opp ved hover.</p>
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				</div>
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">DropdownMenu</h3>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="secondary">Opne meny</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent>
							<DropdownMenuItem>Profil</DropdownMenuItem>
							<DropdownMenuItem>Logg ut</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</div>

			<div className="flex flex-wrap gap-4">
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">Logo</h3>
					<Logo />
				</div>
				<div className="group min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">HouseMark</h3>
					<HouseMark className="h-12 w-12" />
					<p className="text-muted-foreground mt-2 text-xs">
						Hald over boksen: døra går opp.
					</p>
				</div>
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">Spacer</h3>
					<p className="text-muted-foreground text-xs">
						32px høgd mellom tekstane under:
					</p>
					<p>Topp</p>
					<Spacer size="3xs" />
					<p>Bunn</p>
				</div>
			</div>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">CountDown</h3>
				<CountDown date={inDays(30)} />
			</div>

			<div className="flex flex-wrap gap-4">
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">Odometer</h3>
					<OdometerPreview />
					<p className="text-muted-foreground text-xs">
						Sifra rullar når verdien endrar seg (her kvart sekund).
					</p>
				</div>
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">CountUp</h3>
					<p className="text-4xl font-bold">
						<CountUp value={133} />
					</p>
					<p className="text-muted-foreground text-xs">
						Tel opp første gong talet rullar inn i biletet.
					</p>
				</div>
			</div>

			<div className="flex flex-wrap gap-4">
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">ErrorList</h3>
					<ErrorList errors={['Ugyldig e-post', 'For kort']} />
				</div>
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">EpicToaster</h3>
					<EpicToaster />
					<p className="text-muted-foreground text-xs">
						Blir montert her, men toastar dukkar berre opp ved toast()-kall.
					</p>
				</div>
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">EpicProgress</h3>
					<EpicProgress />
					<p className="text-muted-foreground text-xs">
						Fyller seg ut under router-navigasjonar.
					</p>
				</div>
			</div>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">Icon</h3>
				<div className="grid grid-cols-6 gap-4 md:grid-cols-10">
					{iconNames.map((name) => (
						<div
							key={name}
							className="flex flex-col items-center gap-1 text-center"
						>
							<Icon name={name} size="lg" title={name} />
							<code className="text-[10px]">{name}</code>
						</div>
					))}
				</div>
			</div>
		</section>
	)
}

function Molecules() {
	return (
		<section id="molecules" className="flex flex-col gap-8">
			<h2 className="text-2xl font-bold">Molecules</h2>

			<div className="flex flex-wrap gap-4">
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">Field</h3>
					<Field
						labelProps={{ children: 'Namn (pristine)' }}
						inputProps={{ placeholder: 'Kari Nordmann' }}
					/>
					<Field
						labelProps={{ children: 'Namn (med feil)' }}
						inputProps={{ defaultValue: 'K' }}
						errors={['For kort']}
					/>
				</div>
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">TextareaField</h3>
					<TextareaField
						labelProps={{ children: 'Omtale (pristine)' }}
						textareaProps={{ placeholder: 'Skriv noko…' }}
					/>
					<TextareaField
						labelProps={{ children: 'Omtale (med feil)' }}
						textareaProps={{ defaultValue: 'Berre éi linje.' }}
						errors={['Må vere lengre']}
					/>
				</div>
			</div>

			<div className="flex flex-wrap gap-4">
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">CheckboxField</h3>
					<form id="showcase-checkbox-pristine-form">
						<CheckboxField
							labelProps={{ children: 'Bli med (pristine)' }}
							buttonProps={{
								name: 'delta',
								form: 'showcase-checkbox-pristine-form',
								value: 'yes',
							}}
						/>
					</form>
					<form id="showcase-checkbox-error-form">
						<CheckboxField
							labelProps={{ children: 'Bli med (med feil)' }}
							buttonProps={{
								name: 'delta2',
								form: 'showcase-checkbox-error-form',
								value: 'yes',
							}}
							errors={['Du må huke av']}
						/>
					</form>
				</div>
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">OTPField</h3>
					<OTPField
						labelProps={{ children: 'Engangskode (pristine)' }}
						inputProps={{ name: 'code-pristine' }}
					/>
					<OTPField
						labelProps={{ children: 'Engangskode (med feil)' }}
						inputProps={{ name: 'code-error' }}
						errors={['Ugyldig kode']}
					/>
				</div>
			</div>

			<div className="flex flex-wrap gap-4">
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">StatusButton</h3>
					<div className="flex flex-wrap items-center gap-2">
						<StatusButton status="idle">idle</StatusButton>
						<StatusButton status="pending">pending</StatusButton>
						<StatusButton status="success">success</StatusButton>
						<StatusButton status="error">error</StatusButton>
					</div>
				</div>
			</div>

			<div className="flex flex-wrap gap-4">
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">SeatMeter</h3>
					<div className="flex flex-col gap-3">
						<SeatMeter total={20} taken={1} />
						<SeatMeter total={26} taken={23} />
						<SeatMeter total={43} taken={43} />
						<SeatMeter total={120} taken={30} />
					</div>
					<p className="text-muted-foreground text-xs">
						Ledig, få igjen, fullt hus, og stolpe for store rom.
					</p>
				</div>
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">TicketQr</h3>
					<TicketQr hash={demoHash} />
					<p className="text-muted-foreground text-xs">
						QR blir generert på klienten.
					</p>
				</div>
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">ImageField</h3>
					<ImageField name="showcase-image" crop={{ mode: 'free' }} />
					<ImageField
						name="showcase-image-filled"
						initialImageUrl="/img/user.png"
						crop={{ mode: 'crop', aspect: 1, circle: true }}
					/>
					<p className="text-muted-foreground text-xs">
						Cropperen blir opna frå ImageField — krev ei ekte biletfil for å gi
						meining.
					</p>
				</div>
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">InstallAppMenuItem</h3>
					<InstallAppMenuItem />
					<p className="text-muted-foreground text-xs">
						Renderer null utan PWA-installasjonsprompt.
					</p>
				</div>
			</div>
		</section>
	)
}

function Organisms() {
	return (
		<section id="organisms" className="flex flex-col gap-8">
			<h2 className="text-2xl font-bold">Organisms</h2>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">EventCard</h3>
				<div className="flex flex-wrap items-stretch justify-center gap-4">
					<EventCard event={demoEvent} />
					<EventCard event={soldOutEvent} />
					<EventCard event={passedEvent} />
				</div>
				<p className="text-muted-foreground mt-2 text-xs">
					Frå venstre: vanleg; midten: utseld; høgre: avslutta.
				</p>
			</div>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">EventDescription</h3>
				<EventDescription event={demoEvent} />
			</div>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">DetailHero</h3>
				<DetailHero event={demoEvent} />
			</div>

			<div className="flex flex-wrap gap-4">
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">DetailCtaCard</h3>
					<DetailCtaCard event={onSaleEvent} isIOS={false} />
				</div>
				<div className="min-w-56 flex-1 rounded-lg border p-4">
					<h3 className="mb-3 font-semibold">StickyCtaBar</h3>
					<p className="text-muted-foreground text-xs">
						Fixed nedst i viewport — begge variantane (vanleg og utseld) blir
						renderte oppå kvarandre i førehandsvisinga.
					</p>
					<StickyCtaBar
						visible
						soldOut={false}
						seatsLabel="8 ledige"
						tba={false}
					/>
					<StickyCtaBar visible soldOut seatsLabel={null} tba={false} />
				</div>
			</div>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">DoorPassDialog</h3>
				<DoorPassPreview />
			</div>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">TicketWallet</h3>
				<TicketWallet
					canEdit
					concerts={[
						{
							event: {
								id: baseEvent.id,
								title: baseEvent.title,
								date: baseEvent.date,
								location: baseEvent.location,
								host: baseEvent.host,
							},
							passHash: demoHash,
							tickets: [
								{
									id: 'w1',
									type: 'adult',
									name: 'Kari Nordmann',
									used: false,
									status: 'paid',
									price: 250,
								},
								{
									id: 'w2',
									type: 'child',
									name: null,
									used: false,
									status: 'door',
									price: 100,
								},
								{
									id: 'w3',
									type: 'adult',
									name: null,
									used: false,
									status: 'unpaid',
									price: 250,
								},
							],
						},
						{
							event: {
								id: 'showcase-passed',
								title: 'Fjorårets avslutning',
								date: inDays(-300),
								location: 'Naustet',
								host: null,
							},
							passHash: 'aaaabbbbccccdddd',
							tickets: [
								{
									id: 'w4',
									type: 'adult',
									name: 'Kari Nordmann',
									used: true,
									status: 'paid',
									price: 200,
								},
							],
						},
					]}
				/>
				<p className="text-muted-foreground mt-2 text-xs">
					Betalt, betaling i døra, ikkje betalt — og ein fullført konsert.
				</p>
			</div>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">SiteHeader</h3>
				<SiteHeader
					user={{ name: 'Kari Nordmann', username: 'kari', image: null }}
					staffLinks={[]}
					overlay={false}
				/>
				<div className="mt-3 rounded-lg bg-[#121827] pb-2">
					<SiteHeader user={null} staffLinks={[]} overlay />
					<div className="h-12" />
				</div>
			</div>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">SiteDock</h3>
				{/* a transformed box holds the fixed dock in place here */}
				<div className="relative h-28 [transform:translateZ(0)]">
					<SiteDock
						user={{ username: 'kari' }}
						staff="admin"
						unpaidTotal={500}
					/>
				</div>
			</div>

			<FrontPagePreview />

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">ProgramSection</h3>
				<div className="hk">
					<ProgramSection events={festival2024Program} />
				</div>
				<p className="text-muted-foreground mt-2 text-xs">
					2024-programmet: kort med bilete, utseld-stempel, setekart og nattkart
					som følgjer kortet midt på skjermen (breie skjermar).
				</p>
			</div>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">ProgramMap</h3>
				<ProgramMap events={demoProgram} />
			</div>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">NextEventFab</h3>
				<NextEventFab nextEventId="showcase-1" />
				<p className="text-muted-foreground mt-2 text-xs">
					Renderer null før målkortet er utanfor skjermen — knappen dukkar opp
					fixed nede til høgre når ein skrollar forbi.
				</p>
			</div>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">EventForm</h3>
				<fieldset disabled>
					<EventForm
						event={demoEvent}
						onInputChange={() => {}}
						allPerformers={[{ id: demoPerformer.id, name: demoPerformer.name }]}
					/>
				</fieldset>
				<p className="text-muted-foreground mt-2 text-xs">
					Berre førehandsvising — i eit disabled fieldset, kan ikkje sendast.
				</p>
			</div>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">PerformerForm</h3>
				<fieldset disabled>
					<PerformerForm performer={demoPerformerFull} />
				</fieldset>
				<p className="text-muted-foreground mt-2 text-xs">
					Berre førehandsvising — i eit disabled fieldset, kan ikkje sendast.
				</p>
			</div>
		</section>
	)
}

function OdometerPreview() {
	const [value, setValue] = useState(59)
	useEffect(() => {
		const timer = setInterval(
			() => setValue((v) => (v === 0 ? 59 : v - 1)),
			1000,
		)
		return () => clearInterval(timer)
	}, [])
	return (
		<p className="text-5xl font-bold">
			<Odometer value={value} />
		</p>
	)
}

/** The front page's organisms, in page order, with the 2024 program. */
function FrontPagePreview() {
	// same first render on server and client, then the real clock
	const now = useNow(festivalStart.getTime() - 3 * 24 * HOUR)
	return (
		<div className="flex flex-col gap-8">
			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">FestivalHero + FestivalStatus</h3>
				<div className="hk overflow-hidden rounded-lg">
					<FestivalHero
						notice="Admin-førehandsvising: festivalen er i planleggingsmodus for publikum"
						eyebrow="Vorrå, Karmøy · 2024"
						title="Huskonsertfestivalen på Vorrå"
						titleLines={['Huskonsert-', 'festivalen', 'på Vorrå']}
						dates="1.–2. november"
						lede={
							<p>
								Ein festival for heile familien, arrangert av lokale musikantar.
							</p>
						}
						status={<FestivalStatus events={festival2024Program} now={now} />}
						venues={getSceneVenues(festival2024Program)}
					/>
				</div>
				<p className="text-muted-foreground mt-2 text-xs">
					Eitt hus per scene på åsen, eitt lys i vindauget per konsert. Åsen er
					den verkelege profilen frå Helgabergsnuten ned til sjøen (Kartverket).
				</p>
			</div>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">FestivalGate</h3>
				<div className="flex flex-col gap-4">
					<div className="overflow-hidden rounded-lg">
						<FestivalGate
							mode="COUNTDOWN"
							message="Billettane blir sleppte!"
							releaseAt={festivalStart}
							now={now}
						/>
					</div>
					<div className="overflow-hidden rounded-lg">
						<FestivalGate
							mode="PLANNING"
							message={null}
							releaseAt={null}
							now={now}
						/>
					</div>
				</div>
				<p className="text-muted-foreground mt-2 text-xs">
					Det publikum ser når festivalen ikkje er LIVE: nedteljing og
					planlegging (vedlikehald ser ut som planlegging).
				</p>
			</div>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">
					FestivalStatus (under festivalen)
				</h3>
				<div className="hk rounded-lg bg-[#242c43] p-4 text-[#f6efdf]">
					<FestivalStatus
						events={festival2024Program}
						now={new Date(festivalStart.getTime() + HOUR)}
					/>
				</div>
			</div>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">Countdown</h3>
				<div className="hk rounded-lg bg-[#242c43] p-4 text-[#f6efdf]">
					<Countdown target={festivalStart} now={now} label="Dørene opnar om" />
				</div>
			</div>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">FestivalWelcome</h3>
				<div className="hk">
					<FestivalWelcome
						introText={
							'Vorrå Huskonsertfestival er ein lokal musikkfestival på Vorrå-feltet på Karmøy.\n\nHjarteleg velkomen på konsert!'
						}
						events={festival2024Program}
					/>
				</div>
			</div>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">VenueNightMap</h3>
				<div className="hk">
					<VenueNightMap
						events={festival2024Program}
						activeEventId="showcase-f4"
					/>
				</div>
			</div>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">TicketInfo</h3>
				<div className="hk flex flex-col gap-6">
					<TicketInfo festival={festival2024} events={festival2024Program} />
					<TicketInfo
						festival={{
							pricingModel: 'SIMPLE',
							pricePerConcert: 200,
							festivalPassPrice: 500,
						}}
						events={[...festival2024Program, ...festival2024Program.slice(1)]}
					/>
					<TicketInfo
						festival={{ pricingModel: 'SIMPLE' }}
						events={festival2024Program}
					/>
				</div>
				<p className="text-muted-foreground mt-2 text-xs">
					TIERED (2024), SIMPLE med makspris, og prisar som ikkje er sette enno.
				</p>
			</div>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">HelpCard + FestivalFooter</h3>
				<div className="hk">
					<HelpCard
						name="Arne Skorpe Sjøen"
						phone="41104852"
						email="sjoen90@gmail.com"
					/>
					<FestivalFooter />
				</div>
			</div>
		</div>
	)
}

function DoorPassPreview() {
	const [open, setOpen] = useState(true)
	return (
		<>
			<DoorPassDialog
				eventTitle={baseEvent.title}
				open={open}
				onClose={() => setOpen(false)}
				passHash={demoHash}
				lines={[
					{ label: 'Kari Nordmann', status: 'Betalt', tone: 'good' },
					{ label: 'Ordinær', status: 'Betaling i døra', tone: 'warn' },
					{ label: 'Barn', status: 'Gratis', tone: 'good' },
				]}
			/>
			{!open && (
				<button
					type="button"
					className="btn btn-sm btn-outline"
					onClick={() => setOpen(true)}
				>
					Opne dialogen igjen
				</button>
			)}
			<p className="text-muted-foreground mt-2 text-xs">
				Startar open for førehandsvising — X eller Esc lukkar, knappen opnar
				att.
			</p>
		</>
	)
}

function Templates() {
	return (
		<section id="templates" className="flex flex-col gap-8">
			<h2 className="text-2xl font-bold">Templates</h2>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">ErrorMessage</h3>
				<div className="er">
					<ErrorMessage
						code={404}
						title="Her var det ingen heime"
						text="Innhaldet i feilsidene (GeneralErrorBoundary teiknar huset over)."
					/>
				</div>
			</div>

			<div className="rounded-lg border p-4">
				<h3 className="mb-3 font-semibold">EventDetailLayout</h3>
				<EventDetailLayout event={onSaleEvent} isIOS={false} />
				<p className="text-muted-foreground mt-2 text-xs">
					Malen for detaljsida — set saman DetailHero, DetailCtaCard,
					EventDescription og StickyCtaBar i eitt layout. ErrorBoundary og
					Toaster er ikkje med: dei er singletonar som skal renderast éin gong
					per side.
				</p>
			</div>
		</section>
	)
}

const DAISY_THEMES = [
	'huskonsert',
	'retro',
	'light',
	'dark',
	'cupcake',
	'bumblebee',
	'emerald',
	'corporate',
	'synthwave',
	'cyberpunk',
	'valentine',
	'halloween',
	'garden',
	'forest',
	'aqua',
	'lofi',
	'pastel',
	'fantasy',
	'wireframe',
	'black',
	'luxury',
	'dracula',
	'cmyk',
	'autumn',
	'business',
	'acid',
	'lemonade',
	'night',
	'coffee',
	'winter',
	'dim',
	'nord',
	'sunset',
]

export default function Showcase() {
	const [daisyTheme, setDaisyTheme] = useState('huskonsert')
	useEffect(() => {
		const html = document.documentElement
		html.dataset.theme = daisyTheme
		return () => {
			delete html.dataset.theme
		}
	}, [daisyTheme])
	return (
		<div className="mx-auto max-w-4xl px-4 pb-24">
			<h1 className="py-6 text-4xl font-bold">Component Showcase</h1>
			<nav className="bg-background/95 sticky top-0 z-10 flex items-center gap-4 border-b py-3 backdrop-blur">
				<a className="hover:underline" href="#atoms">
					Atoms
				</a>
				<a className="hover:underline" href="#molecules">
					Molecules
				</a>
				<a className="hover:underline" href="#organisms">
					Organisms
				</a>
				<a className="hover:underline" href="#templates">
					Templates
				</a>
				<label className="ml-auto flex items-center gap-2 text-sm">
					Theme
					<select
						className="select select-sm w-40"
						value={daisyTheme}
						onChange={(e) => setDaisyTheme(e.target.value)}
					>
						{DAISY_THEMES.map((t) => (
							<option key={t} value={t}>
								{t}
							</option>
						))}
					</select>
				</label>
			</nav>
			<div className="flex flex-col gap-8 py-8">
				<Atoms />
				<Molecules />
				<Organisms />
				<Templates />
			</div>
		</div>
	)
}
